import { useAuthStore } from '@/stores/auth'
import { useGameStore } from '@/stores/game'
import { usePromotionStore } from '@/stores/promotion'

export type PromotionSyncToast = (opts: {
  title: string
  message: string
  type: 'success' | 'info' | 'error'
}) => void

/**
 * Keeps active promotion in sync for iframe + standalone:
 * partner config/messages, then Bitville claim when source allows.
 */
export function usePromotionSync(toast?: PromotionSyncToast) {
  const promotionStore = usePromotionStore()
  const authStore = useAuthStore()
  const gameStore = useGameStore()

  function applyPartnerPromotion(config?: Record<string, unknown>) {
    const settings = gameStore.getBetSettings
    const modules = settings?.enabled_modules
    const source = promotionStore.resolvePromotionSource(settings ?? undefined)
    const partnerPromo = config?.promotion ?? null

    if (!promotionStore.isModuleEnabled(modules)) {
      if (partnerPromo) {
        promotionStore.clear('module_disabled')
      }
      return
    }

    if (partnerPromo && promotionStore.shouldIgnoreIframePromo(source)) {
      return
    }

    if (partnerPromo) {
      promotionStore.apply(partnerPromo)
      return
    }

    if (!promotionStore.shouldUseBitvilleSource(source)) {
      promotionStore.clear('parent_ready_without_promo')
    }
  }

  async function syncBitvillePromotions(options?: { silent?: boolean }) {
    if (promotionStore.isActive) {
      return
    }

    const settings = gameStore.getBetSettings
    if (!promotionStore.isModuleEnabled(settings?.enabled_modules)) {
      return
    }

    authStore.refreshAuthFromExternalSources()
    if (!authStore.isAuthenticated) {
      return
    }

    if (!promotionStore.shouldUseBitvilleSource(promotionStore.resolvePromotionSource(settings ?? undefined))) {
      return
    }

    const result = await promotionStore.claimFromBitville()
    if (result.applied) {
      return
    }

    if (!options?.silent && toast && result.error) {
      toast({
        title: 'Promotion',
        message: result.error,
        type: 'error',
      })
    }
  }

  async function refreshPromotions(partnerConfig?: Record<string, unknown>, options?: { silent?: boolean }) {
    applyPartnerPromotion(partnerConfig)
    await syncBitvillePromotions(options)
  }

  return {
    applyPartnerPromotion,
    syncBitvillePromotions,
    refreshPromotions,
  }
}
